import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { FloorPlanPanel } from "../FloorPlanPanelNew";
import { FloorPlanPanelPdf } from "../FloorPlanPanelPdf";
import { ViewerModeToggle } from "./ViewerModeToggle";
import { createDisplayTransform } from "./createDisplayTransform";
import {
  scaleDetectedObjects,
  scaleCircuitPaths,
  scaleCablePaths,
  scaleReshapeView,
  scaleRouteCreationOverlay,
  scalePolygonsOverlay,
  scaleFloorPlanLayoutOverlay,
} from "./utils";
import {
  isDetectedObject,
  type FloorPlanPanelHandlers,
  type ZoomState,
} from "../FloorPlanPanelNew/types";
import type { BboxRect } from "../types";
import type { FloorPlanPanelWrapperProps, ViewerMode } from "./types";

export const FloorPlanPanelWrapper = forwardRef<
  FloorPlanPanelHandlers,
  FloorPlanPanelWrapperProps
>(function FloorPlanPanelWrapper(
  {
    plan,
    viewerMode,
    onViewerModeChange,
    imageScaleFactor,
    rotation = 0,
    onRotate,
    detectedObjects,
    selectedObject,
    setSelectedObject,
    backgroundObjects,
    navigationObjects,
    circuitPaths,
    reshapeView,
    circuitBoardMarkers,
    removedMarkers,
    unroutedDeviceMarkers,
    candidateDeviceMarkers,
    selectedDeviceMarkers,
    selectedDeviceMarkersBlink,
    cableTrays,
    highlightTrayType,
    focusedTrayType,
    areRouteLinesHidden,
    highlightMarker,
    heightMarks,
    zoneDraft,
    feedAreas,
    planWalls,
    wallDraft,
    wallSelection,
    assignDeviceBoxes,
    floorPlanCablePaths,
    onMapClick,
    onMapMouseMove,
    onAreaSelect,
    onEmptyMapClick,
    onSelectedBboxChange,
    routeCreationOverlay,
    polygonsOverlay,
    floorPlanLayoutOverlay,
    mode,
    hideViewerModeToggle = false,
    ...rest
  },
  ref,
) {
  const innerRef = useRef<FloorPlanPanelHandlers>(null);
  const pendingZoomRef = useRef<ZoomState | null>(null);
  const clearPendingTimerRef = useRef<number | null>(null);
  const disableTransitionsRef = useRef(false);
  const fittedRotationRef = useRef(rotation);

  const usePdfMode = viewerMode === "pdf";

  const handleViewerModeChange = useCallback(
    (nextMode: ViewerMode) => {
      if (!innerRef.current || !imageScaleFactor) return;

      const currentState = innerRef.current.getZoomState();
      const switchingToPdf = nextMode === "pdf";

      // Image→PDF: zoom *= imageScaleFactor, pan stays the same
      // PDF→Image: zoom /= imageScaleFactor, pan stays the same
      pendingZoomRef.current = {
        zoomLevel: switchingToPdf
          ? currentState.zoomLevel * imageScaleFactor
          : currentState.zoomLevel / imageScaleFactor,
        panOffset: currentState.panOffset,
      };
      disableTransitionsRef.current = true;

      onViewerModeChange(nextMode);
    },
    [imageScaleFactor, onViewerModeChange],
  );

  // Coordinate conversion is needed only for PDF plans rendered in image mode.
  // In that case, element coordinates arrive in PDF space and must be scaled to image space.
  const shouldScaleForImage = !usePdfMode && imageScaleFactor !== 1;

  const displayScaleFactor = shouldScaleForImage ? imageScaleFactor : 1;

  // Rotation turns about the page in display units: image px in image mode, PDF points in PDF mode.
  const transform = useMemo(
    () =>
      createDisplayTransform({
        rotation,
        scaleFactor: displayScaleFactor,
        unrotatedDisplayWidth: usePdfMode
          ? plan.imageWidthPx / imageScaleFactor
          : plan.imageWidthPx,
        unrotatedDisplayHeight: usePdfMode
          ? plan.imageHeightPx / imageScaleFactor
          : plan.imageHeightPx,
      }),
    [
      rotation,
      displayScaleFactor,
      usePdfMode,
      plan.imageWidthPx,
      plan.imageHeightPx,
      imageScaleFactor,
    ],
  );

  const { isIdentity } = transform;

  const displayDetectedObjects = useMemo(
    () =>
      isIdentity
        ? detectedObjects
        : scaleDetectedObjects({ objects: detectedObjects, transform }),
    [isIdentity, detectedObjects, transform],
  );

  const displaySelectedObject = useMemo(
    () =>
      !isIdentity && selectedObject
        ? scaleDetectedObjects({ objects: [selectedObject], transform })[0]
        : selectedObject,
    [isIdentity, selectedObject, transform],
  );

  const displayBackgroundObjects = useMemo(
    () =>
      !isIdentity && backgroundObjects
        ? scaleDetectedObjects({ objects: backgroundObjects, transform })
        : backgroundObjects,
    [isIdentity, backgroundObjects, transform],
  );

  // Only entries with `bbox` (elements) need transforming; routes pass through.
  const displayNavigationObjects = useMemo(() => {
    if (isIdentity || !navigationObjects) return navigationObjects;
    return navigationObjects.map((object) =>
      isDetectedObject(object)
        ? { ...object, bbox: transform.toDisplayBbox(object.bbox) }
        : object,
    );
  }, [isIdentity, navigationObjects, transform]);

  const displayCircuitPaths = useMemo(
    () =>
      !isIdentity && circuitPaths
        ? scaleCircuitPaths({ state: circuitPaths, transform })
        : circuitPaths,
    [isIdentity, circuitPaths, transform],
  );

  // The reshape marks annotate those very cables, so they go through the same
  // transform at the same moment. Built in PDF points and drawn without this, they
  // land in a heap in the top-left of the sheet at 1/px_per_pt.
  const displayReshapeView = useMemo(
    () =>
      !isIdentity && reshapeView
        ? scaleReshapeView({ view: reshapeView, transform })
        : reshapeView,
    [isIdentity, reshapeView, transform],
  );

  const displayBoardMarkers = useMemo(
    () =>
      !isIdentity && circuitBoardMarkers
        ? circuitBoardMarkers.map((marker) => ({
            ...marker,
            ...transform.toDisplay(marker),
          }))
        : circuitBoardMarkers,
    [isIdentity, circuitBoardMarkers, transform],
  );

  const displayRemovedMarkers = useMemo(
    () =>
      !isIdentity && removedMarkers
        ? removedMarkers.map((marker) => ({
            ...marker,
            ...transform.toDisplay(marker),
          }))
        : removedMarkers,
    [isIdentity, removedMarkers, transform],
  );

  // Both ends of every tray, through the same conversion every other position
  // takes. Drawn in PDF points while the markers around them were in image
  // space, the trays would land somewhere else entirely on the sheet.
  const displayCableTrays = useMemo(
    () =>
      !isIdentity && cableTrays
        ? cableTrays.map((tray) => ({
            ...tray,
            from: transform.toDisplay(tray.from),
            to: transform.toDisplay(tray.to),
          }))
        : cableTrays,
    [isIdentity, cableTrays, transform],
  );

  const displayUnroutedDeviceMarkers = useMemo(
    () =>
      !isIdentity && unroutedDeviceMarkers
        ? unroutedDeviceMarkers.map((marker) => ({
            ...marker,
            ...transform.toDisplay(marker),
          }))
        : unroutedDeviceMarkers,
    [isIdentity, unroutedDeviceMarkers, transform],
  );

  const displayCandidateDeviceMarkers = useMemo(
    () =>
      !isIdentity && candidateDeviceMarkers
        ? candidateDeviceMarkers.map(transform.toDisplay)
        : candidateDeviceMarkers,
    [isIdentity, candidateDeviceMarkers, transform],
  );

  const displaySelectedDeviceMarkers = useMemo(
    () =>
      !isIdentity && selectedDeviceMarkers
        ? selectedDeviceMarkers.map(transform.toDisplay)
        : selectedDeviceMarkers,
    [isIdentity, selectedDeviceMarkers, transform],
  );

  const displayHighlightMarker = useMemo(
    () =>
      !isIdentity && highlightMarker
        ? transform.toDisplay(highlightMarker)
        : highlightMarker,
    [isIdentity, highlightMarker, transform],
  );
  const displayHeightMarks = useMemo(
    () =>
      !isIdentity && heightMarks
        ? heightMarks.map((mark) => ({ ...transform.toDisplay(mark), kind: mark.kind }))
        : heightMarks,
    [isIdentity, heightMarks, transform],
  );

  const displayZoneDraft = useMemo(() => {
    if (!zoneDraft || isIdentity) return zoneDraft;
    return {
      points: zoneDraft.points.map(transform.toDisplay),
      cursor: zoneDraft.cursor ? transform.toDisplay(zoneDraft.cursor) : null,
    };
  }, [isIdentity, zoneDraft, transform]);

  const displayFeedAreas = useMemo(
    () =>
      !isIdentity && feedAreas
        ? feedAreas.map((area) => ({
            ...area,
            points: area.points.map(transform.toDisplay),
          }))
        : feedAreas,
    [isIdentity, feedAreas, transform],
  );

  const displayPlanWalls = useMemo(
    () =>
      !isIdentity && planWalls
        ? planWalls.map((wall) => ({
            ...wall,
            points: wall.points.map(transform.toDisplay),
          }))
        : planWalls,
    [isIdentity, planWalls, transform],
  );

  // The draft is in the same space as the clicks that made it, so it transforms
  // the same way the finished wall will.
  const displayWallDraft = useMemo(() => {
    if (!wallDraft) return null;
    return {
      kind: wallDraft.wallKind,
      points: wallDraft.points.map(transform.toDisplay),
      cursor: wallDraft.cursor ? transform.toDisplay(wallDraft.cursor) : null,
    };
  }, [wallDraft, transform]);

  const displayAssignDeviceBoxes = useMemo(
    () =>
      !isIdentity && assignDeviceBoxes
        ? assignDeviceBoxes.map((box) => ({ ...box, bbox: transform.toDisplayBbox(box.bbox) }))
        : assignDeviceBoxes,
    [isIdentity, assignDeviceBoxes, transform],
  );

  const displayCablePaths = useMemo(
    () =>
      !isIdentity && floorPlanCablePaths
        ? scaleCablePaths({ cablePaths: floorPlanCablePaths, transform })
        : floorPlanCablePaths,
    [isIdentity, floorPlanCablePaths, transform],
  );

  const displayRouteCreationOverlay = useMemo(
    () =>
      !isIdentity && routeCreationOverlay
        ? scaleRouteCreationOverlay({
            overlay: routeCreationOverlay,
            transform,
          })
        : routeCreationOverlay,
    [isIdentity, routeCreationOverlay, transform],
  );

  const displayPolygonsOverlay = useMemo(
    () =>
      !isIdentity && polygonsOverlay
        ? scalePolygonsOverlay({ overlay: polygonsOverlay, transform })
        : polygonsOverlay,
    [isIdentity, polygonsOverlay, transform],
  );

  const displayFloorPlanLayoutOverlay = useMemo(
    () =>
      !isIdentity && floorPlanLayoutOverlay
        ? scaleFloorPlanLayoutOverlay({
            overlay: floorPlanLayoutOverlay,
            transform,
          })
        : floorPlanLayoutOverlay,
    [isIdentity, floorPlanLayoutOverlay, transform],
  );

  // Clicks arrive in display space; convert them back to PDF space before passing upstream.
  const wrappedOnMapClick = useCallback(
    (pos: { x: number; y: number }) => {
      if (!onMapClick) return;
      onMapClick(transform.fromDisplay(pos));
    },
    [onMapClick, transform],
  );

  const wrappedOnMapMouseMove = useCallback(
    (pos: { x: number; y: number } | null) => {
      if (!onMapMouseMove) return;
      onMapMouseMove(pos ? transform.fromDisplay(pos) : pos);
    },
    [onMapMouseMove, transform],
  );

  // A rotated plan turns the rectangle; its PDF-space box is the box around all four corners.
  const wrappedOnAreaSelect = useMemo(() => {
    if (!onAreaSelect) return undefined;
    return (rect: { x1: number; y1: number; x2: number; y2: number; isAdditive?: boolean }) => {
      const corners = [
        { x: rect.x1, y: rect.y1 },
        { x: rect.x2, y: rect.y1 },
        { x: rect.x1, y: rect.y2 },
        { x: rect.x2, y: rect.y2 },
      ].map((corner) => transform.fromDisplay(corner));
      onAreaSelect({
        x1: Math.min(...corners.map((corner) => corner.x)),
        y1: Math.min(...corners.map((corner) => corner.y)),
        x2: Math.max(...corners.map((corner) => corner.x)),
        y2: Math.max(...corners.map((corner) => corner.y)),
        isAdditive: rect.isAdditive,
      });
    };
  }, [onAreaSelect, transform]);
  const wrappedOnSelectedBboxChange = useMemo(() => {
    if (!onSelectedBboxChange) return undefined;
    return (bbox: BboxRect) =>
      onSelectedBboxChange(transform.fromDisplayBbox(bbox));
  }, [onSelectedBboxChange, transform]);

  const wrappedOnEmptyMapClick = useCallback(
    (pos: { x: number; y: number }) => {
      if (!onEmptyMapClick) return;
      onEmptyMapClick(transform.fromDisplay(pos));
    },
    [onEmptyMapClick, transform],
  );

  // Wrap imperative handles so callers can pass PDF-space positions
  // and they get converted to display space for the panels.
  useImperativeHandle(
    ref,
    () => ({
      zoomToFit: () => innerRef.current?.zoomToFit(),
      zoomToObject: (object) => {
        innerRef.current?.zoomToObject(transform.toDisplay(object));
      },
      zoomToBbox: (bbox) => {
        innerRef.current?.zoomToBbox(transform.toDisplayBbox(bbox));
      },
      zoomToElements: (args) => {
        if (!innerRef.current) return;
        innerRef.current.zoomToElements({
          ...args,
          elements: args.elements.map(transform.toDisplay),
        });
      },
      flashCanvas: (duration) => innerRef.current?.flashCanvas(duration),
      flashRipple: () => innerRef.current?.flashRipple(),
      getZoomState: () =>
        innerRef.current?.getZoomState() ?? {
          zoomLevel: 1,
          panOffset: { x: 0, y: 0 },
        },
      setZoomState: (state) => innerRef.current?.setZoomState(state),
    }),
    [transform],
  );

  // After mode switch, apply the saved zoom state once the new panel is ready.
  // Uses a ref so zoomToFitOnImageLoad stays "never" without re-render flipping it back.
  useEffect(() => {
    if (!pendingZoomRef.current) return;

    const savedState = pendingZoomRef.current;

    // Apply zoom state shortly after the new panel mounts,
    // then re-enable transitions once the state is applied.
    const applyTimeoutId = setTimeout(() => {
      innerRef.current?.setZoomState(savedState);
      disableTransitionsRef.current = false;
    }, 50);

    // Clear the ref after enough time for the panel to fully load,
    // so zoomToFitOnImageLoad stays "never" during the entire load cycle.
    if (clearPendingTimerRef.current !== null) {
      clearTimeout(clearPendingTimerRef.current);
    }
    clearPendingTimerRef.current = window.setTimeout(() => {
      pendingZoomRef.current = null;
      clearPendingTimerRef.current = null;
    }, 3000);

    return () => clearTimeout(applyTimeoutId);
  }, [usePdfMode]);

  // The only re-fit on rotation: the panels' own fits miss image mode, 180, and the "never" fit mode.
  useEffect(() => {
    if (fittedRotationRef.current === rotation) return;
    fittedRotationRef.current = rotation;
    innerRef.current?.zoomToFit();
  }, [rotation]);

  const isModeSwitching = !!pendingZoomRef.current;

  const sharedProps = {
    ...rest,
    mode,
    rotation,
    onRotate,
    // Suppress auto zoom-to-fit and transitions when restoring zoom state from mode switch.
    // pendingZoomRef is set synchronously before the mode change re-render,
    // so on the mount render it's already "never".
    zoomToFitOnImageLoad: isModeSwitching ? "never" : rest.zoomToFitOnImageLoad,
    disableTransitions: disableTransitionsRef.current,
    detectedObjects: displayDetectedObjects,
    selectedObject: displaySelectedObject,
    setSelectedObject,
    backgroundObjects: displayBackgroundObjects,
    navigationObjects: displayNavigationObjects,
    circuitPaths: displayCircuitPaths,
    reshapeView: displayReshapeView,
    circuitBoardMarkers: displayBoardMarkers,
    removedMarkers: displayRemovedMarkers,
    unroutedDeviceMarkers: displayUnroutedDeviceMarkers,
    selectedDeviceMarkers: displaySelectedDeviceMarkers,
    candidateDeviceMarkers: displayCandidateDeviceMarkers,
    selectedDeviceMarkersBlink,
    cableTrays: displayCableTrays,
    highlightTrayType,
    focusedTrayType,
    areRouteLinesHidden,
    highlightMarker: displayHighlightMarker,
    heightMarks: displayHeightMarks,
    zoneDraft: displayZoneDraft,
    feedAreas: displayFeedAreas,
    planWalls: displayPlanWalls,
    wallDraft: displayWallDraft,
    wallSelection,
    assignDeviceBoxes: displayAssignDeviceBoxes,
    floorPlanCablePaths: displayCablePaths,
    onMapClick: wrappedOnMapClick,
    onMapMouseMove: wrappedOnMapMouseMove,
    onAreaSelect: wrappedOnAreaSelect,
    onEmptyMapClick: wrappedOnEmptyMapClick,
    onSelectedBboxChange: wrappedOnSelectedBboxChange,
    routeCreationOverlay: displayRouteCreationOverlay,
    polygonsOverlay: displayPolygonsOverlay,
    floorPlanLayoutOverlay: displayFloorPlanLayoutOverlay,
  };

  return (
    <div className="relative h-full w-full">
      {usePdfMode ? (
        <FloorPlanPanelPdf
          ref={innerRef}
          plan={{
            pdfUrl: plan.pdfUrl,
            pageNumber: plan.pageNumber ?? 1,
            name: plan.name,
            isArchived: plan.isArchived,
          }}
          {...sharedProps}
        />
      ) : (
        <FloorPlanPanel
          ref={innerRef}
          plan={{
            previewUrl: plan.previewUrl,
            name: plan.name,
            imageWidthPx: plan.imageWidthPx,
            imageHeightPx: plan.imageHeightPx,
            isArchived: plan.isArchived,
          }}
          imageScaleFactor={shouldScaleForImage ? imageScaleFactor : undefined}
          skipInitialLoadingState={isModeSwitching}
          {...sharedProps}
        />
      )}
      {!hideViewerModeToggle && plan.pdfUrl && (
        <ViewerModeToggle
          viewerMode={viewerMode}
          onViewerModeChange={handleViewerModeChange}
        />
      )}
    </div>
  );
});
